// Ported from files/snapdraw-design-kit/code/src/lib/i18n/strings/documents.ts
// (docs.index.*, docs.new.*, docs.quote.*, docs.app.* match the real
// DocumentsPage/NewDocumentPage/QuoteBuilderPage/G702BuilderPage field-for-
// field). A few kit strings referenced fixture-only content (a fake
// "Riverside Tower App #4" duplicate-source, a fake "last edited" date) and
// were replaced with generic real equivalents; save-status messages,
// breadcrumbs and aria-labels the kit's static mockups didn't need are new.
// 2026-08-28 (backlog LAUNCH-5): docs.new.format.quote.body claimed "Send for
// e-signature" — no e-signature service is wired up (only a physical
// signature line on the quote's acceptance block). Replaced with honest
// "Build and send a PDF" copy, same two-sentence structure/length.
// 2026-08-28 (backlog DOCS-23): the Quote/Invoice format cards' meta line
// claimed "PDF + EMAIL" — SnapDraw has no email-delivery feature (see
// docs.sent.body's own honest disclaimer, "SnapDraw does not send anything
// for you"). Replaced with "PDF EXPORT," the capability that actually
// exists.
export const en = {
  "docs.index.eyebrow": "All Documents",
  "docs.index.everything": "Everything",
  "docs.index.searchPlaceholder": "Search documents, clients, amounts…",
  "docs.index.exportCsv": "Export CSV",
  "docs.index.new": "New",
  "docs.index.clearClient": "Clear client filter ×",
  "docs.index.clearAging": "Clear {bucket} filter ×",
  "docs.index.col.type": "Type",
  "docs.index.col.document": "Document",
  "docs.index.col.client": "Client",
  "docs.index.col.date": "Date",
  "docs.index.col.amount": "Amount",
  "docs.index.col.status": "Status",
  "docs.index.col.actions": "Actions",
  "docs.index.loading": "Loading documents",
  "docs.index.empty.title": "Nothing in this view",
  "docs.index.empty.body": "No documents match this filter yet. Try another status or start a new document.",
  "docs.index.empty.action": "New document",
  "docs.index.footer.count": "{count} Documents",
  "docs.index.loadSample": "Load sample data",
  "docs.index.loadingSample": "Loading…",
  "docs.index.clearSample": "Clear sample data",
  "docs.index.clearSampleConfirm": "Remove all sample documents? This won't touch anything you created yourself.",
  "docs.index.sampleError": "Something went wrong with sample data.",
  "docs.index.actionError": "Something went wrong. Please try again.",

  "doc.due": "Due",
  "docs.late": "{days}d late",
  "docs.expired": "Expired {days}d ago",
  "docs.field.projectName": "Project / Site Name",
  "docs.move.aria": "Change status",
  "docs.move.title": "Move to",
  "docs.move.to.draft": "Draft",
  "docs.move.to.pending": "Pending",
  "docs.move.to.accepted": "Accepted",
  "docs.move.to.closed": "Closed",
  "docs.move.to.void": "Void",
  "docs.due.field": "Payment terms",
  "docs.due.change": "Change due date",
  "docs.due.terms.receipt": "Due on receipt",
  "docs.due.terms.net15": "Net 15",
  "docs.due.terms.net30": "Net 30",
  "docs.due.terms.net45": "Net 45",
  "docs.due.validUntil": "Valid until",
  "docs.due.paymentDue": "Payment due",
  "docs.paid.title.quote": "Mark paid",
  "docs.paid.title.payment": "Payment received",
  "docs.paid.body": "When did the money actually land? We use this date for your cash timeline, not the day you closed it out.",
  "docs.paid.bodyNamed": "When did payment for {name} actually land? We use this date for your cash timeline, not the day you closed it out.",
  "docs.paid.quick": "Quick pick",
  "docs.paid.pick.today": "Today",
  "docs.paid.pick.yesterday": "Yesterday",
  "docs.paid.pick.week": "Last week",
  "docs.paid.onQuote": "Paid on",
  "docs.paid.onApplication": "Payment received",
  "docs.paid.hint": "Back-date it if you closed out late",
  "docs.paid.cancel": "Cancel",
  "docs.paid.confirm": "Mark paid {date}",

  "docs.new.breadcrumb": "Documents · New",
  "docs.new.back": "Back",
  "docs.new.step": "Document type",
  "docs.new.title": "What are we drawing up?",
  "docs.new.sub": "Pick a format. We'll pre-fill your crew, tax rate and retainage.",
  "docs.new.format.quote.title": "Standard Quote",
  "docs.new.format.quote.tag": "Fast",
  "docs.new.format.quote.body": "Line items, materials and labor. Build and send a PDF in under two minutes.",
  "docs.new.format.quote.meta": "~2 MIN · PDF EXPORT",
  "docs.new.format.aia.title": "G702 / G703",
  "docs.new.format.aia.tag": "AIA",
  "docs.new.format.aia.body": "Application & certificate for payment with continuation sheet and retainage math.",
  "docs.new.format.aia.meta": "~6 MIN · GC-READY FORMAT",
  "docs.new.format.invoice.title": "Invoice",
  "docs.new.format.invoice.tag": "Bill",
  "docs.new.format.invoice.body": "A final bill for time & materials — standalone, or converted from an accepted quote.",
  "docs.new.format.invoice.meta": "~2 MIN · PDF EXPORT",
  "docs.new.format.contract.title": "Contract",
  "docs.new.format.contract.tag": "Setup",
  "docs.new.format.contract.body": "Set up the approved schedule of values once — every pay application on the job carries it forward.",
  "docs.new.format.contract.meta": "~5 MIN · ONE-TIME SETUP",
  "docs.new.continue.heading": "Continue a contract",
  "docs.new.continue.all": "All contracts",
  "docs.new.continue.next": "App #{n}",
  "docs.new.continue.through": "Billed through App #{n}",
  "docs.new.continue.first": "No applications yet",
  "docs.new.continue.empty.title": "No active contracts yet",
  "docs.new.continue.empty.body": "Set up a contract and its next application will show up here.",
  "docs.new.recentTemplates": "Recently used templates",
  "docs.new.templates.manage": "Manage templates",
  "docs.new.templates.usedCount": "Used {n}×",
  "docs.new.empty.title": "No templates yet",
  "docs.new.empty.body": "Save any quote, invoice or pay application as a template and it will be one tap away on your next job.",

  "docs.quote.breadcrumb": "Documents · Standard Quote",
  "docs.quote.title": "Standard Quote",
  "docs.quote.panel.details": "Quote Details",
  "docs.quote.field.number": "Quote number",
  "docs.quote.number.hint": "Auto-generated — editable if you run your own numbering.",
  "docs.quote.number.taken": "That number is already used on another quote.",
  "docs.quote.number.lockedRevision": "Inherited from the original quote — only the revision number changes.",
  "docs.quote.validity.field": "Pricing holds for",
  "docs.quote.validity.d15": "15 days",
  "docs.quote.validity.d30": "30 days",
  "docs.quote.validity.d60": "60 days",
  "docs.quote.validity.until": "Valid until {date}",
  "docs.quote.terms.field": "Payment terms",
  "docs.quote.terms.needsClient": "Select a client first.",
  "docs.quote.terms.from": "{terms} — from {name}",
  "docs.quote.terms.change": "Change for this quote",
  "docs.quote.terms.keep": "Keep client's terms",
  "docs.quote.panel.business": "Your Business",
  "docs.quote.field.businessName": "Business name",
  "docs.quote.field.phone": "Phone",
  "docs.quote.field.email": "Email",
  "docs.quote.panel.client": "Client",
  "docs.quote.panel.rates": "Rates & Tax",
  "docs.quote.field.laborRate": "Default labor rate ($/hr)",
  "docs.quote.field.materialsMarkup": "Materials markup (%)",
  "docs.quote.field.salesTax": "Sales tax (%)",
  "docs.quote.markup.hint": "Added on top of your catalog cost for materials and equipment. A change here is remembered for this client.",
  "docs.quote.markup.forClient": "Default {pct}% — saved for {name}",
  "docs.quote.markup.overridden": "Your markup, saved for this client",
  "docs.quote.markup.reset": "Reset to your {pct}% default",
  "docs.quote.lineItems": "Line Items",
  "docs.quote.col.description": "Description",
  "docs.quote.col.type": "Type",
  "docs.quote.col.qtyHrs": "Qty / Hrs",
  "docs.quote.col.rate": "Rate",
  "docs.quote.col.markup": "Markup",
  "docs.quote.col.total": "Total",
  "docs.quote.empty.title": "No line items yet",
  "docs.quote.empty.body": "Add labor hours or materials and SnapDraw totals the markup, tax and balance for you.",
  "docs.quote.addLabor": "Labor line",
  "docs.quote.addMaterial": "Material line",
  "docs.quote.total.laborSubtotal": "Labor subtotal",
  "docs.quote.total.due": "Quote Total",
  "docs.quote.scope.title": "Scope & Exclusions",
  "docs.quote.scope.body": "What's included and what isn't — the paragraph a GC reads before signing.",
  "docs.quote.scope.work": "Scope of work",
  "docs.quote.scope.workPlaceholder": "Describe the work included in this quote…",
  "docs.quote.scope.exclusions": "Exclusions",
  "docs.quote.scope.exclusionsPlaceholder": "What's not included…",
  "docs.quote.scope.preset": "Add common exclusions",
  "docs.quote.accept.title": "Acceptance",
  "docs.quote.accept.body": "Sign below to accept this quote as written.",
  "docs.quote.accept.signature": "Client signature",
  "docs.quote.accept.date": "Date",
  "docs.quote.accept.printName": "Print name",
  "docs.quote.accept.printTitle": "Title",
  "docs.quote.save": "Save quote",
  "docs.quote.action.cancel": "Cancel",

  "docs.revise.builderTitle": "Revise Quote",
  "docs.revise.title": "Revise this quote",
  "docs.revise.body": "Creates a new draft from {number}. The original stays on file and only moves to Superseded once you send this revision.",
  "docs.revise.reasonLabel": "Reason for revision",
  "docs.revise.reasonPlaceholder": "e.g. Rock encountered at 3 ft — additional trenching",
  "docs.revise.reasonRequired": "A reason is required — it prints on the revised quote.",
  "docs.revise.confirm": "Create revision",
  "docs.revise.cancel": "Cancel",

  "docs.invoice.breadcrumb": "Documents · Invoice",
  "docs.invoice.title": "Invoice",

  "docs.lineage.aria": "Document history",
  "docs.lineage.title": "History",

  "docs.delta.title": "What changed",
  "docs.delta.noChange": "No change",
  "docs.delta.lines": "{count} line changes",
  "docs.delta.kind.added": "Added",
  "docs.delta.kind.removed": "Removed",
  "docs.delta.kind.changed": "Changed",
  "docs.delta.reason": "Reason for revision",

  "docs.variance.title": "Invoice vs. quote",
  "docs.variance.note": "Why the total differs",

  "docs.app.breadcrumb": "Documents · G702 / G703",
  "docs.app.step": "Application for payment",
  "docs.app.title": "G702 / G703",
  "docs.app.panel.gc": "General Contractor / Owner",
  "docs.app.gc.first": "Pick who you are billing. Their contract terms, retainage and job address follow onto the form.",
  "docs.app.gc.none": "No client selected. Pay applications attach to a client record so terms and retainage stay accurate.",
  "docs.app.panel.contract": "Contract",
  "docs.app.contract.first": "Start from the contract. The schedule of values, retainage and prior billings come with it.",
  "docs.app.contract.choose": "Choose contract",
  "docs.app.contract.change": "Change",
  "docs.app.contract.unlink": "Detach from contract",
  "docs.app.contract.unlinked": "Application detached from {name}. Contract values are now editable.",
  "docs.app.contract.linked": "{name} loaded — App #{n}, schedule of values and prior billings carried forward.",
  "docs.app.contract.appNo": "Application #{n}",
  "docs.app.contract.executed": "Executed {date}",
  "docs.app.contract.changeOrders": "{count} executed change order(s)",
  "docs.app.contract.noChangeOrders": "No change orders executed",
  "docs.app.contract.locked": "From the contract. Change orders are the only way these move.",
  "docs.app.contract.freeform": "No contract linked — values typed by hand.",
  "docs.app.contract.carried": "Carried forward from App #{n}",
  "docs.app.contract.carriedNone": "First application — nothing carried forward.",
  "docs.app.contract.addLineNote": "Extra lines outside the contract will not foot to the contract sum. Execute a change order instead.",
  "docs.app.panel.period": "Application & Period",
  "docs.app.field.number": "Application number",
  "docs.app.number.hint": "Handed to you in sequence. Edit it if you run your own numbering.",
  "docs.app.number.taken": "That application number is already issued.",
  "docs.app.field.periodFrom": "Period from",
  "docs.app.field.periodTo": "Period to",
  "docs.app.field.contractDate": "Contract date",
  "docs.app.panel.summary": "Contract Summary",
  "docs.app.field.originalSum": "Original contract sum ($)",
  "docs.app.field.netChange": "Net change by change orders ($)",
  "docs.app.field.retainage": "Retainage (%)",
  "docs.app.retainage.hint": "Held on completed work and stored materials, per the contract.",
  "docs.app.panel.contractor": "Contractor",
  "docs.app.field.businessName": "Business name",
  "docs.app.field.phone": "Phone",
  "docs.app.field.email": "Email",
  "docs.app.business.summaryEdit": "Change on this application",
  "docs.app.sov.title": "Schedule of Values",
  "docs.app.sov.sub": "G703 continuation",
  "docs.app.line.description": "Description of work",
  "docs.app.line.namePlaceholder": "e.g. Rough-in — branch circuits",
  "docs.app.line.scheduled": "Scheduled",
  "docs.app.line.previous": "Previous",
  "docs.app.line.thisPeriod": "This period",
  "docs.app.line.stored": "Stored",
  "docs.app.line.toDate": "To date",
  "docs.app.line.balance": "Balance",
  "docs.app.line.remove": "Remove line",
  "docs.app.line.overbilled": "Over scheduled value",
  "docs.app.line.complete": "{pct}% complete",
  "docs.app.line.n": "Line {n}",
  "docs.app.reconcile.ok": "Schedule of values foots to the contract sum.",
  "docs.app.reconcile.over": "Schedule of values is {amount} over the contract sum to date.",
  "docs.app.reconcile.under": "Schedule of values is {amount} under the contract sum to date.",
  "docs.app.empty.title": "No line items yet",
  "docs.app.empty.body": "Add each scheduled item of work — SnapDraw carries previous billings forward and calculates retainage automatically.",
  "docs.app.addLine": "Line item",
  "docs.app.total.originalSum": "Original contract sum",
  "docs.app.total.netChange": "Net change by change orders",
  "docs.app.total.contractSumToDate": "Contract sum to date",
  "docs.app.total.completedStored": "Total completed & stored to date",
  "docs.app.total.completedNote": "{pct}% of contract sum to date",
  "docs.app.total.retainagePct": "Retainage ({pct}%)",
  "docs.app.total.retainageNote": "{completed} completed · {stored} stored",
  "docs.app.total.earnedLessRetainage": "Total earned less retainage",
  "docs.app.total.lessPreviousCerts": "Less previous certificates",
  "docs.app.total.lessPreviousNote": "Derived from prior billings — edit to match the GC's ledger",
  "docs.app.total.currentDue": "Current Payment Due",
  "docs.app.total.balanceToFinish": "Balance to finish, incl. retainage",
  "docs.app.save": "Save application",
  "docs.app.done": "Done",

  "docs.removeLine": "Remove line",
  "docs.save.saving": "Saving…",
  "docs.save.saved": "Saved {docId} at {time}",
  "docs.save.errorWithMessage": "Could not save — {message}",
  "docs.save.error": "Could not save.",

  "docs.view.backAria": "Back to documents",
  "docs.view.rev": "Rev {rev}",
  "docs.view.deliveredOn": "Delivered {date}",
  "docs.view.download": "Download PDF",
  "docs.view.print": "Print",
  "docs.view.printing": "Generating PDF…",
  "docs.view.printError": "Could not generate the PDF. Please try again.",
  "docs.view.edit": "Edit",
  "docs.view.revisionToast": "Editing creates Rev {rev} — re-deliver the PDF after saving",
  "docs.view.action.revise": "Revise",
  "docs.view.action.createInvoice": "Create Invoice",
  "docs.view.action.openInvoice": "Open Invoice",
  "docs.view.provenance.replaces": "Replaces {number}",
  "docs.view.provenance.bills": "Bills approved quote {number}",
  "docs.view.kind.quote": "Quote",
  "docs.view.kind.invoice": "Invoice",
  "docs.view.kind.application": "Application for payment",
  "docs.view.billTo": "Bill to",
  "docs.view.terms": "Terms",
  "docs.view.paymentTerms": "Payment terms",
  "docs.view.noDueDate": "No due date until sent",
  "docs.view.amountDue": "Quote total",
  "docs.view.amountDueInvoice": "Amount due",
  "docs.view.col.unit": "Unit",
  "docs.view.total.subtotal": "Subtotal",
  "docs.view.total.retainage": "Retainage held ({pct}%)",
  "docs.view.total.due": "Quote Total",
  "docs.view.total.dueInvoice": "Invoice Total",
  "docs.view.footer": "Generated with SnapDraw",
  "docs.view.notice.draft": "Draft — nothing has left SnapDraw yet. Edit freely, then download or print to deliver it.",
  "docs.view.notice.pending": "Delivered to your client. Editing bumps the revision number, so download and re-deliver the new PDF.",
  "docs.view.notice.accepted": "Accepted and locked. Changes to signed work go out as a change order, not an edit.",
  "docs.view.notice.closed": "Closed and locked. Kept for your records and your cash timeline.",
  "docs.view.notice.void": "Voided. Excluded from every total; restore it as a draft to work on it again.",
  "docs.view.notice.superseded": "Replaced by a newer revision. Kept for your records — see the history above for what replaced it.",
  "docs.view.notice.invoiced": "Billed to a real invoice, which is now the receivable. Kept for your records — see the history above.",
  "docs.view.missing.title": "Document not found",
  "docs.view.missing.body": "That document may have been deleted or the link is wrong.",
  "docs.view.missing.action": "Back to documents",
  "docs.view.viewAndPrint": "View & Print",

  "docs.sent.title": "Mark as sent?",
  "docs.sent.body": "SnapDraw does not send anything for you. Once you have emailed or handed {name} to your client, mark it sent so the due date starts counting.",
  "docs.sent.confirm": "Yes, I sent it",
  "docs.sent.notYet": "Not yet",
  "docs.sent.toast": "{name} marked sent {date}",

  "aia.formTitle": "AIA Document G702 · Application and Certificate for Payment",
  "aia.docTitle": "Application for payment",
  "aia.appNo": "Application no. {no}",
  "aia.continued": "Continued",
  "aia.toOwner": "To owner",
  "aia.fromContractor": "From contractor",
  "aia.project": "Project",
  "aia.viaArchitect": "Via architect",
  "aia.notApplicable": "N/A",
  "aia.periodTo": "Period to",
  "aia.contractFor": "Contract for",
  "aia.pendingDate": "Set on delivery",
  "aia.summaryTitle": "Contractor's application for payment",
  "aia.l1": "Original contract sum",
  "aia.l2": "Net change by change orders",
  "aia.l3": "Contract sum to date (line 1 ± 2)",
  "aia.l4": "Total completed & stored to date ({pct}% complete)",
  "aia.l5": "Retainage",
  "aia.l5a": "{pct}% of completed work",
  "aia.l5b": "{pct}% of stored material",
  "aia.l6": "Total earned less retainage (line 4 − 5)",
  "aia.l7": "Less previous certificates for payment",
  "aia.l8": "Current payment due",
  "aia.l9": "Balance to finish, including retainage (line 3 − 6)",
  "aia.co.additions": "Change orders — additions",
  "aia.co.deductions": "Change orders — deductions",
  "aia.co.net": "Net change by change orders",
  "aia.cert.title": "Contractor's certification",
  "aia.cert.body":
    "The undersigned Contractor certifies that to the best of the Contractor's knowledge, information and belief the work covered by this Application for Payment has been completed in accordance with the Contract Documents, that all amounts have been paid by the Contractor for work for which previous Certificates for Payment were issued and payments received from the Owner, and that current payment shown herein is now due.",
  "aia.cert.contractor": "Contractor",
  "aia.cert.date": "Date",
  "aia.notary.title": "Notary",
  "aia.notary.state": "State of",
  "aia.notary.county": "County of",
  "aia.notary.body": "Subscribed and sworn to before me this date.",
  "aia.notary.public": "Notary public",
  "aia.notary.expires": "My commission expires",
  "aia.arch.title": "Architect's certificate for payment",
  "aia.arch.body":
    "In accordance with the Contract Documents and based on on-site observations and the data comprising this application, the Architect certifies to the Owner that the work has progressed as indicated, the quality of the work is in accordance with the Contract Documents, and the Contractor is entitled to payment of the amount certified.",
  "aia.arch.amountCertified": "Amount certified",
  "aia.arch.architect": "Architect",
  "aia.footer": "G702 cover page · Continuation sheet G703 attached · Generated with SnapDraw",
  "aia.g703.form": "AIA Document G703",
  "aia.g703.title": "Continuation sheet",
  "aia.g703.colA": "A · Item",
  "aia.g703.colB": "B · Description",
  "aia.g703.colC": "C · Scheduled value",
  "aia.g703.colD": "D · Previous",
  "aia.g703.colE": "E · This period",
  "aia.g703.colF": "F · Stored",
  "aia.g703.colG": "G · Completed + stored",
  "aia.g703.colPct": "%",
  "aia.g703.colH": "H · Balance",
  "aia.g703.colI": "I · Retainage",
  "aia.g703.totals": "Totals",
  "aia.g703.footer": "Columns D + E + F equal column G, which carries to line 4 of the G702 cover page.",
} as const;

export const es: Record<keyof typeof en, string> = {
  "docs.index.eyebrow": "Todos los documentos",
  "docs.index.everything": "Todo",
  "docs.index.searchPlaceholder": "Buscar documentos, clientes, montos…",
  "docs.index.exportCsv": "Exportar CSV",
  "docs.index.new": "Nuevo",
  "docs.index.clearClient": "Quitar filtro de cliente ×",
  "docs.index.clearAging": "Quitar filtro {bucket} ×",
  "docs.index.col.type": "Tipo",
  "docs.index.col.document": "Documento",
  "docs.index.col.client": "Cliente",
  "docs.index.col.date": "Fecha",
  "docs.index.col.amount": "Monto",
  "docs.index.col.status": "Estado",
  "docs.index.col.actions": "Acciones",
  "docs.index.loading": "Cargando documentos",
  "docs.index.empty.title": "Nada en esta vista",
  "docs.index.empty.body": "Ningún documento coincide con este filtro. Prueba otro estado o crea un documento nuevo.",
  "docs.index.empty.action": "Nuevo documento",
  "docs.index.footer.count": "{count} documentos",
  "docs.index.loadSample": "Cargar datos de muestra",
  "docs.index.loadingSample": "Cargando…",
  "docs.index.clearSample": "Borrar datos de muestra",
  "docs.index.clearSampleConfirm": "¿Eliminar todos los documentos de muestra? Esto no afectará nada que hayas creado tú.",
  "docs.index.sampleError": "Algo salió mal con los datos de muestra.",
  "docs.index.actionError": "Algo salió mal. Inténtalo de nuevo.",

  "doc.due": "Vence",
  "docs.late": "{days}d atraso",
  "docs.expired": "Expiró hace {days}d",
  "docs.field.projectName": "Nombre del proyecto / sitio",
  "docs.move.aria": "Cambiar estado",
  "docs.move.title": "Mover a",
  "docs.move.to.draft": "Borrador",
  "docs.move.to.pending": "Pendiente",
  "docs.move.to.accepted": "Aceptado",
  "docs.move.to.closed": "Cerrado",
  "docs.move.to.void": "Anulado",
  "docs.due.field": "Términos de pago",
  "docs.due.change": "Cambiar fecha de vencimiento",
  "docs.due.terms.receipt": "Al recibir",
  "docs.due.terms.net15": "Neto 15",
  "docs.due.terms.net30": "Neto 30",
  "docs.due.terms.net45": "Neto 45",
  "docs.due.validUntil": "Válido hasta",
  "docs.due.paymentDue": "Pago vence",
  "docs.paid.title.quote": "Marcar pagado",
  "docs.paid.title.payment": "Pago recibido",
  "docs.paid.body": "¿Cuándo entró el dinero realmente? Usamos esta fecha para tu línea de cobros, no el día en que lo cerraste.",
  "docs.paid.bodyNamed": "¿Cuándo entró el pago de {name}? Usamos esta fecha para tu línea de cobros, no el día en que lo cerraste.",
  "docs.paid.quick": "Selección rápida",
  "docs.paid.pick.today": "Hoy",
  "docs.paid.pick.yesterday": "Ayer",
  "docs.paid.pick.week": "Semana pasada",
  "docs.paid.onQuote": "Pagado el",
  "docs.paid.onApplication": "Pago recibido",
  "docs.paid.hint": "Ajusta la fecha si cerraste tarde",
  "docs.paid.cancel": "Cancelar",
  "docs.paid.confirm": "Marcar pagado {date}",

  "docs.new.breadcrumb": "Documentos · Nuevo",
  "docs.new.back": "Volver",
  "docs.new.step": "Tipo de documento",
  "docs.new.title": "¿Qué vamos a redactar?",
  "docs.new.sub": "Elige un formato. Prellenamos tu cuadrilla, impuesto y retención.",
  "docs.new.format.quote.title": "Cotización estándar",
  "docs.new.format.quote.tag": "Rápido",
  "docs.new.format.quote.body": "Partidas, materiales y mano de obra. Crea y envía un PDF en menos de dos minutos.",
  "docs.new.format.quote.meta": "~2 MIN · EXPORTAR PDF",
  "docs.new.format.aia.title": "G702 / G703",
  "docs.new.format.aia.tag": "AIA",
  "docs.new.format.aia.body": "Solicitud y certificado de pago con hoja de continuación y cálculo de retención.",
  "docs.new.format.aia.meta": "~6 MIN · FORMATO LISTO PARA GC",
  "docs.new.format.invoice.title": "Factura",
  "docs.new.format.invoice.tag": "Cobro",
  "docs.new.format.invoice.body": "Una factura final por tiempo y materiales — independiente, o convertida desde una cotización aceptada.",
  "docs.new.format.invoice.meta": "~2 MIN · EXPORTAR PDF",
  "docs.new.format.contract.title": "Contrato",
  "docs.new.format.contract.tag": "Configurar",
  "docs.new.format.contract.body": "Configura una vez el desglose de valores aprobado — cada solicitud de pago del trabajo lo hereda automáticamente.",
  "docs.new.format.contract.meta": "~5 MIN · CONFIGURACIÓN ÚNICA",
  "docs.new.continue.heading": "Continuar un contrato",
  "docs.new.continue.all": "Todos los contratos",
  "docs.new.continue.next": "Solicitud #{n}",
  "docs.new.continue.through": "Facturado hasta la solicitud #{n}",
  "docs.new.continue.first": "Aún no hay solicitudes",
  "docs.new.continue.empty.title": "Aún no hay contratos activos",
  "docs.new.continue.empty.body": "Configura un contrato y su próxima solicitud aparecerá aquí.",
  "docs.new.recentTemplates": "Plantillas usadas recientemente",
  "docs.new.templates.manage": "Administrar plantillas",
  "docs.new.templates.usedCount": "Usada {n}×",
  "docs.new.empty.title": "Aún no hay plantillas",
  "docs.new.empty.body": "Guarda cualquier cotización, factura o solicitud de pago como plantilla y estará a un toque en tu próximo trabajo.",

  "docs.quote.breadcrumb": "Documentos · Cotización estándar",
  "docs.quote.title": "Cotización estándar",
  "docs.quote.panel.details": "Detalles de la cotización",
  "docs.quote.field.number": "Número de cotización",
  "docs.quote.number.hint": "Generado automáticamente — editable si usas tu propia numeración.",
  "docs.quote.number.taken": "Ese número ya se usó en otra cotización.",
  "docs.quote.number.lockedRevision": "Heredado de la cotización original — solo cambia el número de revisión.",
  "docs.quote.validity.field": "Precio válido por",
  "docs.quote.validity.d15": "15 días",
  "docs.quote.validity.d30": "30 días",
  "docs.quote.validity.d60": "60 días",
  "docs.quote.validity.until": "Válido hasta {date}",
  "docs.quote.terms.field": "Términos de pago",
  "docs.quote.terms.needsClient": "Elige un cliente primero.",
  "docs.quote.terms.from": "{terms} — según {name}",
  "docs.quote.terms.change": "Cambiar para esta cotización",
  "docs.quote.terms.keep": "Mantener los términos del cliente",
  "docs.quote.panel.business": "Tu negocio",
  "docs.quote.field.businessName": "Nombre del negocio",
  "docs.quote.field.phone": "Teléfono",
  "docs.quote.field.email": "Correo",
  "docs.quote.panel.client": "Cliente",
  "docs.quote.panel.rates": "Tarifas e impuestos",
  "docs.quote.field.laborRate": "Tarifa de mano de obra ($/hr)",
  "docs.quote.field.materialsMarkup": "Margen de materiales (%)",
  "docs.quote.field.salesTax": "Impuesto sobre ventas (%)",
  "docs.quote.markup.hint": "Se agrega sobre el costo de tu catálogo para materiales y equipo. Un cambio aquí se recuerda para este cliente.",
  "docs.quote.markup.forClient": "Predeterminado {pct}% — guardado para {name}",
  "docs.quote.markup.overridden": "Tu margen, guardado para este cliente",
  "docs.quote.markup.reset": "Restablecer a tu {pct}% predeterminado",
  "docs.quote.lineItems": "Partidas",
  "docs.quote.col.description": "Descripción",
  "docs.quote.col.type": "Tipo",
  "docs.quote.col.qtyHrs": "Cant. / Hrs",
  "docs.quote.col.rate": "Tarifa",
  "docs.quote.col.markup": "Margen",
  "docs.quote.col.total": "Total",
  "docs.quote.empty.title": "Aún no hay partidas",
  "docs.quote.empty.body": "Agrega horas de mano de obra o materiales y SnapDraw calcula el margen, impuesto y saldo por ti.",
  "docs.quote.addLabor": "Línea de mano de obra",
  "docs.quote.addMaterial": "Línea de material",
  "docs.quote.total.laborSubtotal": "Subtotal mano de obra",
  "docs.quote.total.due": "Total de la cotización",
  "docs.quote.scope.title": "Alcance y exclusiones",
  "docs.quote.scope.body": "Qué incluye y qué no — el párrafo que un contratista general lee antes de firmar.",
  "docs.quote.scope.work": "Alcance del trabajo",
  "docs.quote.scope.workPlaceholder": "Describe el trabajo incluido en esta cotización…",
  "docs.quote.scope.exclusions": "Exclusiones",
  "docs.quote.scope.exclusionsPlaceholder": "Qué no está incluido…",
  "docs.quote.scope.preset": "Agregar exclusiones comunes",
  "docs.quote.accept.title": "Aceptación",
  "docs.quote.accept.body": "Firma abajo para aceptar esta cotización tal como está redactada.",
  "docs.quote.accept.signature": "Firma del cliente",
  "docs.quote.accept.date": "Fecha",
  "docs.quote.accept.printName": "Nombre en letra de molde",
  "docs.quote.accept.printTitle": "Puesto",
  "docs.quote.save": "Guardar cotización",
  "docs.quote.action.cancel": "Cancelar",

  "docs.revise.builderTitle": "Revisar cotización",
  "docs.revise.title": "Revisar esta cotización",
  "docs.revise.body": "Crea un nuevo borrador a partir de {number}. La original se conserva y solo pasa a Reemplazada cuando envíes esta revisión.",
  "docs.revise.reasonLabel": "Motivo de la revisión",
  "docs.revise.reasonPlaceholder": "p. ej. Se encontró roca a 3 pies — zanja adicional",
  "docs.revise.reasonRequired": "Se requiere un motivo — se imprime en la cotización revisada.",
  "docs.revise.confirm": "Crear revisión",
  "docs.revise.cancel": "Cancelar",

  "docs.invoice.breadcrumb": "Documentos · Factura",
  "docs.invoice.title": "Factura",

  "docs.lineage.aria": "Historial del documento",
  "docs.lineage.title": "Historial",

  "docs.delta.title": "Qué cambió",
  "docs.delta.noChange": "Sin cambios",
  "docs.delta.lines": "{count} cambios de línea",
  "docs.delta.kind.added": "Agregada",
  "docs.delta.kind.removed": "Eliminada",
  "docs.delta.kind.changed": "Cambiada",
  "docs.delta.reason": "Motivo de la revisión",

  "docs.variance.title": "Factura vs. cotización",
  "docs.variance.note": "Por qué difiere el total",

  "docs.app.breadcrumb": "Documentos · G702 / G703",
  "docs.app.step": "Solicitud de pago",
  "docs.app.title": "G702 / G703",
  "docs.app.panel.gc": "Contratista general / Propietario",
  "docs.app.gc.first": "Elige a quién le facturas. Sus términos, retención y dirección pasan al formulario.",
  "docs.app.gc.none": "Sin cliente. Las solicitudes de pago se ligan a un cliente para mantener términos y retención correctos.",
  "docs.app.panel.contract": "Contrato",
  "docs.app.contract.first": "Empieza por el contrato. El programa de valores, la retención y la facturación previa vienen con él.",
  "docs.app.contract.choose": "Elegir contrato",
  "docs.app.contract.change": "Cambiar",
  "docs.app.contract.unlink": "Desvincular del contrato",
  "docs.app.contract.unlinked": "Solicitud desvinculada de {name}. Los valores del contrato ya son editables.",
  "docs.app.contract.linked": "{name} cargado — solicitud #{n}, programa de valores y facturación previa arrastrados.",
  "docs.app.contract.appNo": "Solicitud #{n}",
  "docs.app.contract.executed": "Firmado el {date}",
  "docs.app.contract.changeOrders": "{count} orden(es) de cambio firmadas",
  "docs.app.contract.noChangeOrders": "Sin órdenes de cambio",
  "docs.app.contract.locked": "Viene del contrato. Solo cambia con una orden de cambio.",
  "docs.app.contract.freeform": "Sin contrato vinculado — valores escritos a mano.",
  "docs.app.contract.carried": "Arrastrado de la solicitud #{n}",
  "docs.app.contract.carriedNone": "Primera solicitud — nada arrastrado.",
  "docs.app.contract.addLineNote": "Las líneas fuera del contrato no cuadrarán con la suma del contrato. Use una orden de cambio en su lugar.",
  "docs.app.panel.period": "Solicitud y periodo",
  "docs.app.field.number": "Número de solicitud",
  "docs.app.number.hint": "Se te asigna en secuencia. Edítalo si usas tu propia numeración.",
  "docs.app.number.taken": "Ese número de solicitud ya fue emitido.",
  "docs.app.field.periodFrom": "Periodo desde",
  "docs.app.field.periodTo": "Periodo hasta",
  "docs.app.field.contractDate": "Fecha del contrato",
  "docs.app.panel.summary": "Resumen del contrato",
  "docs.app.field.originalSum": "Suma original del contrato ($)",
  "docs.app.field.netChange": "Cambio neto por órdenes de cambio ($)",
  "docs.app.field.retainage": "Retención (%)",
  "docs.app.retainage.hint": "Retenido sobre trabajo completado y material almacenado, según el contrato.",
  "docs.app.panel.contractor": "Contratista",
  "docs.app.field.businessName": "Nombre del negocio",
  "docs.app.field.phone": "Teléfono",
  "docs.app.field.email": "Correo",
  "docs.app.business.summaryEdit": "Cambiar para esta solicitud",
  "docs.app.sov.title": "Programa de valores",
  "docs.app.sov.sub": "Continuación G703",
  "docs.app.line.description": "Descripción del trabajo",
  "docs.app.line.namePlaceholder": "ej. Instalación — circuitos derivados",
  "docs.app.line.scheduled": "Programado",
  "docs.app.line.previous": "Anterior",
  "docs.app.line.thisPeriod": "Este periodo",
  "docs.app.line.stored": "Almacenado",
  "docs.app.line.toDate": "A la fecha",
  "docs.app.line.balance": "Saldo",
  "docs.app.line.remove": "Eliminar partida",
  "docs.app.line.overbilled": "Excede el valor programado",
  "docs.app.line.complete": "{pct}% completado",
  "docs.app.line.n": "Partida {n}",
  "docs.app.reconcile.ok": "El programa de valores cuadra con la suma del contrato.",
  "docs.app.reconcile.over": "El programa de valores excede por {amount} la suma del contrato a la fecha.",
  "docs.app.reconcile.under": "Al programa de valores le faltan {amount} para la suma del contrato a la fecha.",
  "docs.app.empty.title": "Aún no hay partidas",
  "docs.app.empty.body": "Agrega cada partida programada — SnapDraw traslada lo facturado antes y calcula la retención automáticamente.",
  "docs.app.addLine": "Partida",
  "docs.app.total.originalSum": "Suma original del contrato",
  "docs.app.total.netChange": "Cambio neto por órdenes de cambio",
  "docs.app.total.contractSumToDate": "Suma del contrato a la fecha",
  "docs.app.total.completedStored": "Total completado y almacenado a la fecha",
  "docs.app.total.completedNote": "{pct}% de la suma del contrato a la fecha",
  "docs.app.total.retainagePct": "Retención ({pct}%)",
  "docs.app.total.retainageNote": "{completed} completado · {stored} almacenado",
  "docs.app.total.earnedLessRetainage": "Total ganado menos retención",
  "docs.app.total.lessPreviousCerts": "Menos certificados anteriores",
  "docs.app.total.lessPreviousNote": "Derivado de facturaciones previas — edítalo para cuadrar con el GC",
  "docs.app.total.currentDue": "Pago actual adeudado",
  "docs.app.total.balanceToFinish": "Saldo para terminar, incl. retención",
  "docs.app.save": "Guardar solicitud",
  "docs.app.done": "Listo",

  "docs.removeLine": "Eliminar línea",
  "docs.save.saving": "Guardando…",
  "docs.save.saved": "Se guardó {docId} a las {time}",
  "docs.save.errorWithMessage": "No se pudo guardar — {message}",
  "docs.save.error": "No se pudo guardar.",

  "docs.view.backAria": "Volver a documentos",
  "docs.view.rev": "Rev {rev}",
  "docs.view.deliveredOn": "Entregado {date}",
  "docs.view.download": "Descargar PDF",
  "docs.view.print": "Imprimir",
  "docs.view.printing": "Generando PDF…",
  "docs.view.printError": "No se pudo generar el PDF. Inténtalo de nuevo.",
  "docs.view.edit": "Editar",
  "docs.view.revisionToast": "Editar crea la Rev {rev} — vuelve a entregar el PDF al guardar",
  "docs.view.action.revise": "Revisar",
  "docs.view.action.createInvoice": "Crear factura",
  "docs.view.action.openInvoice": "Abrir factura",
  "docs.view.provenance.replaces": "Reemplaza a {number}",
  "docs.view.provenance.bills": "Factura la cotización aprobada {number}",
  "docs.view.kind.quote": "Cotización",
  "docs.view.kind.invoice": "Factura",
  "docs.view.kind.application": "Solicitud de pago",
  "docs.view.billTo": "Facturar a",
  "docs.view.terms": "Términos",
  "docs.view.paymentTerms": "Términos de pago",
  "docs.view.noDueDate": "Sin vencimiento hasta enviarlo",
  "docs.view.amountDue": "Total de la cotización",
  "docs.view.amountDueInvoice": "Monto adeudado",
  "docs.view.col.unit": "Unidad",
  "docs.view.total.subtotal": "Subtotal",
  "docs.view.total.retainage": "Retención acumulada ({pct}%)",
  "docs.view.total.due": "Total de la cotización",
  "docs.view.total.dueInvoice": "Total de la factura",
  "docs.view.footer": "Generado con SnapDraw",
  "docs.view.notice.draft": "Borrador — todavía no ha salido de SnapDraw. Edítalo y luego descárgalo o imprímelo para entregarlo.",
  "docs.view.notice.pending": "Entregado a tu cliente. Al editarlo sube el número de revisión: descarga y vuelve a entregar el nuevo PDF.",
  "docs.view.notice.accepted": "Aceptado y bloqueado. Los cambios al trabajo firmado se hacen con una orden de cambio.",
  "docs.view.notice.closed": "Cerrado y bloqueado. Se conserva para tu historial y tu línea de efectivo.",
  "docs.view.notice.void": "Anulado. Excluido de todos los totales; restáuralo como borrador para volver a trabajarlo.",
  "docs.view.notice.superseded": "Reemplazada por una revisión más reciente. Se conserva para tu historial — mira el historial arriba para ver qué la reemplazó.",
  "docs.view.notice.invoiced": "Facturada a una factura real, que ahora es la cuenta por cobrar. Se conserva para tu historial — mira el historial arriba.",
  "docs.view.missing.title": "Documento no encontrado",
  "docs.view.missing.body": "Puede que se haya eliminado o que el enlace sea incorrecto.",
  "docs.view.missing.action": "Volver a documentos",
  "docs.view.viewAndPrint": "Ver e imprimir",

  "docs.sent.title": "¿Marcar como enviado?",
  "docs.sent.body": "SnapDraw no envía nada por ti. Cuando hayas enviado o entregado {name} a tu cliente, márcalo como enviado para que empiece a contar el vencimiento.",
  "docs.sent.confirm": "Sí, lo envié",
  "docs.sent.notYet": "Todavía no",
  "docs.sent.toast": "{name} marcado como enviado {date}",

  "aia.formTitle": "Documento AIA G702 · Solicitud y certificado de pago",
  "aia.docTitle": "Solicitud de pago",
  "aia.appNo": "Solicitud n.° {no}",
  "aia.continued": "Continuación",
  "aia.toOwner": "Al propietario",
  "aia.fromContractor": "Del contratista",
  "aia.project": "Proyecto",
  "aia.viaArchitect": "Vía arquitecto",
  "aia.notApplicable": "N/D",
  "aia.periodTo": "Periodo hasta",
  "aia.contractFor": "Contrato por",
  "aia.pendingDate": "Se fija al entregar",
  "aia.summaryTitle": "Solicitud de pago del contratista",
  "aia.l1": "Suma original del contrato",
  "aia.l2": "Cambio neto por órdenes de cambio",
  "aia.l3": "Suma del contrato a la fecha (línea 1 ± 2)",
  "aia.l4": "Total completado y almacenado a la fecha ({pct}% completado)",
  "aia.l5": "Retención",
  "aia.l5a": "{pct}% del trabajo completado",
  "aia.l5b": "{pct}% del material almacenado",
  "aia.l6": "Total ganado menos retención (línea 4 − 5)",
  "aia.l7": "Menos certificados de pago anteriores",
  "aia.l8": "Pago actual adeudado",
  "aia.l9": "Saldo para terminar, incluida la retención (línea 3 − 6)",
  "aia.co.additions": "Órdenes de cambio — adiciones",
  "aia.co.deductions": "Órdenes de cambio — deducciones",
  "aia.co.net": "Cambio neto por órdenes de cambio",
  "aia.cert.title": "Certificación del contratista",
  "aia.cert.body":
    "El contratista que suscribe certifica que, según su leal saber y entender, el trabajo cubierto por esta solicitud de pago se ha completado conforme a los documentos del contrato, que todos los montos por trabajos con certificados de pago anteriores han sido pagados por el contratista y recibidos del propietario, y que el pago aquí indicado se adeuda ahora.",
  "aia.cert.contractor": "Contratista",
  "aia.cert.date": "Fecha",
  "aia.notary.title": "Notario",
  "aia.notary.state": "Estado de",
  "aia.notary.county": "Condado de",
  "aia.notary.body": "Suscrito y jurado ante mí en esta fecha.",
  "aia.notary.public": "Notario público",
  "aia.notary.expires": "Mi comisión vence",
  "aia.arch.title": "Certificado de pago del arquitecto",
  "aia.arch.body":
    "De acuerdo con los documentos del contrato y con base en las observaciones en obra y los datos de esta solicitud, el arquitecto certifica al propietario que el trabajo ha avanzado según lo indicado, que su calidad cumple con los documentos del contrato y que el contratista tiene derecho al pago del monto certificado.",
  "aia.arch.amountCertified": "Monto certificado",
  "aia.arch.architect": "Arquitecto",
  "aia.footer": "Portada G702 · Hoja de continuación G703 adjunta · Generado con SnapDraw",
  "aia.g703.form": "Documento AIA G703",
  "aia.g703.title": "Hoja de continuación",
  "aia.g703.colA": "A · Partida",
  "aia.g703.colB": "B · Descripción",
  "aia.g703.colC": "C · Valor programado",
  "aia.g703.colD": "D · Anterior",
  "aia.g703.colE": "E · Este periodo",
  "aia.g703.colF": "F · Almacenado",
  "aia.g703.colG": "G · Completado + almacenado",
  "aia.g703.colPct": "%",
  "aia.g703.colH": "H · Saldo",
  "aia.g703.colI": "I · Retención",
  "aia.g703.totals": "Totales",
  "aia.g703.footer": "Las columnas D + E + F equivalen a la columna G, que pasa a la línea 4 de la portada G702.",
};
